import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {{ id: number, title: string, price: number, available: boolean, tracked: boolean, stock: number, image: string | null }} PairVariant
 * @typedef {{ productId: number, currency: string, moneyFormat: string, variants: PairVariant[] }} PairData
 */

/**
 * « Compose ta paire »: two radio groups (first and second earring) and one
 * button that adds both earrings to the cart in a single request.
 *
 * Choices that would need more than the stock left (stock minus what is
 * already in the cart) are disabled. Shopify still checks the stock on add
 * and at checkout, so this is a comfort layer, not the guarantee.
 */
class PyComposePair extends HTMLElement {
  /** @type {PairData | null} */
  #data = null;

  /** @type {Map<number, number>} variant id → quantity already in the cart */
  #inCart = new Map();

  #busy = false;

  connectedCallback() {
    const json = this.querySelector('script[data-py-pair-data]');
    if (!json?.textContent) return;

    try {
      this.#data = JSON.parse(json.textContent);
    } catch (error) {
      console.error('[py-compose-pair] Invalid data', error);
      return;
    }

    this.addEventListener('change', this.#onChange);
    this.querySelector('[data-add]')?.addEventListener('click', this.#onAdd);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);

    this.#render();
    this.#syncCart();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.querySelector('[data-add]')?.removeEventListener('click', this.#onAdd);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
  }

  #onChange = () => {
    this.#setMessage('');
    this.#render();
  };

  /** Keeps the stock left in step when the cart changes elsewhere (drawer, other forms). */
  #onCartLinesUpdate = (/** @type {CartLinesUpdateEvent} */ event) => {
    if (event.target === this) return;
    event.promise?.then(() => this.#syncCart()).catch(() => {});
  };

  /** @param {1 | 2} slot */
  #selected(slot) {
    const input = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`[data-slot="${slot}"] input:checked`)
    );
    return input ? Number(input.value) : null;
  }

  /** @param {PairVariant} variant */
  #left(variant) {
    if (!variant.available) return 0;
    if (!variant.tracked) return Infinity;
    return variant.stock - (this.#inCart.get(variant.id) ?? 0);
  }

  #render() {
    if (!this.#data) return;

    for (const slot of /** @type {const} */ ([1, 2])) {
      const other = this.#selected(slot === 1 ? 2 : 1);

      for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (
        this.querySelectorAll(`[data-slot="${slot}"] input`)
      )) {
        const variant = this.#variant(Number(input.value));
        if (!variant) continue;

        const left = this.#left(variant) - (other === variant.id ? 1 : 0);
        const disabled = left < 1;

        input.disabled = disabled;
        if (disabled) input.checked = false;

        const soldOut = input.parentElement?.querySelector('[data-soldout]');
        if (soldOut instanceof HTMLElement) soldOut.hidden = this.#left(variant) >= 1;
      }
    }

    const first = this.#variant(this.#selected(1));
    const second = this.#variant(this.#selected(2));

    this.#preview(1, first);
    this.#preview(2, second);

    const total = this.querySelector('[data-total]');
    if (total) {
      const price1 = first?.price ?? second?.price ?? this.#data.variants[0]?.price ?? 0;
      const price2 = second?.price ?? first?.price ?? this.#data.variants[0]?.price ?? 0;
      total.textContent = formatMoney(price1 + price2, this.#data.moneyFormat, this.#data.currency);
    }

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-add]'));
    if (button) button.disabled = this.#busy || !first || !second;
  }

  /**
   * @param {1 | 2} slot
   * @param {PairVariant | null} variant
   */
  #preview(slot, variant) {
    const item = /** @type {HTMLElement | null} */ (this.querySelector(`[data-preview="${slot}"]`));
    if (!item) return;
    item.style.backgroundImage = variant?.image ? `url("${variant.image}")` : '';
  }

  /** @param {number | null} id */
  #variant(id) {
    if (id === null || !this.#data) return null;
    return this.#data.variants.find((variant) => variant.id === id) ?? null;
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** @param {{ items?: Array<{ variant_id: number, quantity: number }> }} cart */
  #setCart(cart) {
    this.#inCart = new Map();
    for (const item of cart.items ?? []) {
      this.#inCart.set(item.variant_id, (this.#inCart.get(item.variant_id) ?? 0) + item.quantity);
    }
    this.#render();
  }

  async #syncCart() {
    try {
      this.#setCart(await this.#fetchCart());
    } catch (error) {
      console.warn('[py-compose-pair]', error);
    }
  }

  #onAdd = async () => {
    const first = this.#selected(1);
    const second = this.#selected(2);
    if (!this.#data || this.#busy || !first || !second) return;

    const items =
      first === second
        ? [{ id: first, quantity: 2 }]
        : [
            { id: first, quantity: 1 },
            { id: second, quantity: 1 },
          ];

    const sectionIds = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.push(element.dataset.sectionId);
    }

    this.#busy = true;
    this.#setMessage('');
    this.#render();

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    const detail = {
      source: 'py-compose-pair',
      sourceId: this.id,
      itemCount: 2,
      productId: String(this.#data.productId),
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((res) => res.json());

      const cart = await this.#fetchCart();

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, items: cart.items, didError: true },
        });
        this.#setMessage(response.description || this.dataset.errorText || '', 'error');
      } else {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, items: cart.items, sections: response.sections, didError: false },
        });
        this.#setMessage(this.dataset.addedText || '');
      }

      this.#busy = false;
      this.#setCart(cart);
    } catch (error) {
      console.error('[py-compose-pair]', error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#busy = false;
      this.#setMessage(this.dataset.errorText || '', 'error');
      this.#render();
    }
  };

  /**
   * @param {string} text
   * @param {'error'} [state]
   */
  #setMessage(text, state) {
    const message = /** @type {HTMLElement | null} */ (this.querySelector('[data-message]'));
    if (!message) return;
    message.textContent = text;
    if (state) message.dataset.state = state;
    else delete message.dataset.state;
  }
}

if (!customElements.get('py-compose-pair')) {
  customElements.define('py-compose-pair', PyComposePair);
}
